/**
 * API hook for the reference library.
 *
 * Upload is a 3-step flow (spec): POST /library/upload-url -> presigned S3
 * PUT -> POST /library/ingest. Any failing step throws; no silent fallbacks.
 * Hook-originated failures throw CODED errors (i18n keys like
 * "library.uploadFailed") so the panel can translate them; backend error
 * strings pass through verbatim. list/getItem/waitForReady accept an
 * optional AbortSignal; an aborted waitForReady resolves null silently —
 * aborting is not an error. waitForReady's default timeout (12 min)
 * deliberately exceeds the worker's 10-minute timeout, so "stuck" genuinely
 * means a dead worker.
 */
import { useCallback } from "react";
import { API_BASE } from "../../config";
import { useAuth } from "../../auth/AuthProvider";
import { mapItem, type LibraryDocument } from "./types";

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const DEFAULT_POLL_MAX_MS = 12 * 60_000;
const DEFAULT_POLL_STEP_MS = 2_500;

export function useLibraryApi() {
  const { ensureToken } = useAuth();

  const apiJson = useCallback(async <T,>(path: string, init: RequestInit = {}): Promise<T> => {
    const token = await ensureToken();
    const resp = await fetch(`${API_BASE}${path}`, {
      ...init,
      headers: { Authorization: `Bearer ${token}`, ...(init.headers || {}) },
    });
    const text = await resp.text();
    let data: any = {};
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      throw new Error(`HTTP ${resp.status} (non-JSON response)`);
    }
    if (!resp.ok) throw new Error(data?.error || `HTTP ${resp.status}`);
    return data as T;
  }, [ensureToken]);

  const list = useCallback(async (signal?: AbortSignal): Promise<LibraryDocument[]> => {
    const data = await apiJson<{ items?: any[] }>("/library", { signal });
    return (Array.isArray(data.items) ? data.items : []).map(mapItem);
  }, [apiJson]);

  const getItem = useCallback(async (docId: string, signal?: AbortSignal): Promise<{ doc: LibraryDocument; downloadUrl?: string }> => {
    const qs = new URLSearchParams({ doc_id: docId });
    const data = await apiJson<{ item?: any; download_url?: string }>(`/library/item?${qs}`, { signal });
    if (!data.item) throw new Error("Malformed response: missing item");
    return { doc: mapItem(data.item), downloadUrl: data.download_url };
  }, [apiJson]);

  const remove = useCallback(async (docId: string) => {
    const qs = new URLSearchParams({ doc_id: docId });
    return apiJson(`/library/item?${qs}`, { method: "DELETE" });
  }, [apiJson]);

  const uploadFile = useCallback(async (file: File, title: string): Promise<string> => {
    const { doc_id, upload_url } = await apiJson<{ doc_id: string; upload_url: string }>(
      "/library/upload-url",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ filename: file.name }),
      },
    );

    const putResp = await fetch(upload_url, {
      method: "PUT",
      headers: { "Content-Type": "application/octet-stream" },
      body: file,
    });
    if (!putResp.ok) {
      // Log the real HTTP status for diagnostics, then throw the coded error
      // the panel translates.
      console.error("[useLibraryApi] S3 upload failed:", putResp.status);
      throw new Error("library.uploadFailed");
    }

    await apiJson("/library/ingest", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ doc_id, filename: file.name, title }),
    });
    return doc_id;
  }, [apiJson]);

  const waitForReady = useCallback(async (
    docId: string,
    onUpdate: (doc: LibraryDocument) => void,
    opts?: { maxMs?: number; stepMs?: number; signal?: AbortSignal },
  ): Promise<LibraryDocument | null> => {
    const maxMs = opts?.maxMs ?? DEFAULT_POLL_MAX_MS;
    const stepMs = opts?.stepMs ?? DEFAULT_POLL_STEP_MS;
    const signal = opts?.signal;
    const t0 = Date.now();
    for (;;) {
      if (signal?.aborted) return null; // caller unmounted — not an error
      const { doc } = await getItem(docId, signal);
      onUpdate(doc);
      if (doc.status === "ready" || doc.status === "error") return doc;
      if (Date.now() - t0 >= maxMs) {
        throw new Error("library.stuckMessage");
      }
      await sleep(stepMs);
    }
  }, [getItem]);

  return { list, getItem, remove, uploadFile, waitForReady };
}
